import { FC } from "react";

import { useBlocNext } from "@blac/react";
import styled from "@emotion/styled";
import { getSupportedUserLocale } from "src/lib/i18next";
import translate from "src/lib/translate";
import MealsStatsCubit, {
  macroFillFraction
} from "src/state/MealsStatsCubit/MealsStatsCubit";
import Translate from "src/ui/components/Translate/Translate";
import { Ring } from "@9amhealth/shared";

import ProteinTargetBadge from "./ProteinTargetBadge";

/** The 9am "sunrise" gradient (orange → pink → blue), used for the ring and bars. */
const SUNRISE_GRADIENT_STOPS = ["#FFBD70", "#F7BDE6", "#80AEFF"];

const Block = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  background: var(--color-cream-darker, #f2efe7);
  border-radius: 1.5rem;
  padding: 1.5rem 0;

  &[data-with-card="true"] {
    border-radius: 0 0 1.5rem 1.5rem;
    padding: 3rem 0 1.5rem;
  }
`;

const Label = styled.p`
  margin: 0;
  color: var(--color-charcoal-60);
  font-size: 0.65625rem;
  font-weight: 700;
  line-height: 1.4;
  letter-spacing: 0.42px;
  text-transform: uppercase;
`;

const ProteinRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 0 1.5rem 0 1.25rem;
`;

const ProteinText = styled.div`
  flex: 1 0 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const RingValue = styled.p`
  margin: 0;
  color: var(--color-charcoal, #212121);
  letter-spacing: -0.32px;
`;

const RingAmount = styled.span`
  font-family: "STK Bureau Serif", serif;
  font-size: 1.625rem;
  font-weight: 300;
  line-height: 1.75rem;
  letter-spacing: -0.52px;
`;

const RingUnit = styled.span`
  font-size: 0.875rem;
  letter-spacing: -0.28px;
  color: var(--color-charcoal-60);
`;

const ProteinDescription = styled.p`
  margin: 0;
  color: var(--color-charcoal, #212121);
  font-size: 0.875rem;
  line-height: 1.43;
  letter-spacing: -0.28px;
`;

const Tiles = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1rem 1.5rem;
`;

const Tile = styled.div`
  flex: 1 0 0;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  min-width: 0;
`;

const Bar = styled.div`
  position: relative;
  height: 4px;
  border-radius: 8px;
  overflow: hidden;
  background-image: linear-gradient(
    90deg,
    #ffbd70 0%,
    #f7bde6 47%,
    #80aeff 100%
  );
`;

const BarCover = styled.div<{ $fraction: number }>`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: ${(props) => (1 - props.$fraction) * 100}%;
  background: var(--color-cream, #fffcf3);
`;

const TileValue = styled.p`
  margin: 0;
  color: var(--color-charcoal, #212121);
  letter-spacing: -0.32px;
`;

const TileAmount = styled.span`
  font-family: "STK Bureau Serif", serif;
  font-size: 1.25rem;
  font-weight: 300;
  line-height: 1.75rem;
  letter-spacing: -0.4px;
`;

const TileUnit = styled.span`
  font-size: 0.875rem;
  letter-spacing: -0.28px;
  color: var(--color-charcoal-60);
`;

interface MacroTileConfig {
  key: "fiber" | "calories";
  labelKey:
    | "mealInsights.today.macro.fiber"
    | "mealInsights.today.macro.calories";
  amount: string;
  unit: string;
  fraction: number;
}

interface MealTodayStatsProps {
  /** Whether the insights card overlaps this block from above. */
  withCard: boolean;
}

const MealTodayStats: FC<MealTodayStatsProps> = ({ withCard }) => {
  const [{ protein, fiber, calories }] = useBlocNext(MealsStatsCubit);

  const proteinFraction = macroFillFraction(protein.total, protein.floor);
  const proteinTargetMet = protein.floor > 0 && proteinFraction >= 1;

  const tiles: MacroTileConfig[] = [
    {
      key: "fiber",
      labelKey: "mealInsights.today.macro.fiber",
      amount: `${Math.round(fiber.total)}`,
      unit: `/${Math.round(fiber.floor)}g`,
      fraction: macroFillFraction(fiber.total, fiber.floor)
    },
    {
      key: "calories",
      labelKey: "mealInsights.today.macro.calories",
      amount: Math.round(calories.total).toLocaleString(
        getSupportedUserLocale()
      ),
      unit:
        calories.target > 0
          ? `/${Math.round(calories.target).toLocaleString(
              getSupportedUserLocale()
            )}kcal`
          : "kcal",
      fraction: macroFillFraction(calories.total, calories.target)
    }
  ];

  return (
    <Block data-with-card={withCard ? "true" : "false"}>
      <ProteinRow>
        {proteinTargetMet ? (
          <ProteinTargetBadge
            aria-label={translate("mealInsights.today.proteinLabel")}
          />
        ) : (
          <Ring
            progress={proteinFraction}
            progressGradient={SUNRISE_GRADIENT_STOPS}
          >
            <RingValue>
              <RingAmount>{Math.round(protein.total)}</RingAmount>
              <RingUnit>g</RingUnit>
            </RingValue>
          </Ring>
        )}
        <ProteinText>
          <Label>
            <Translate msg="mealInsights.today.proteinLabel" />
          </Label>
          <ProteinDescription>
            {proteinTargetMet
              ? translate("mealInsights.today.proteinDone", {
                  total: Math.round(protein.total)
                })
              : translate("mealInsights.today.proteinDescription", {
                  floor: Math.round(protein.floor)
                })}
          </ProteinDescription>
        </ProteinText>
      </ProteinRow>

      <Tiles>
        {tiles.map((tile) => (
          <Tile key={tile.key}>
            <Label>
              <Translate msg={tile.labelKey} />
            </Label>
            <Bar>
              <BarCover $fraction={tile.fraction} />
            </Bar>
            <TileValue>
              <TileAmount>{tile.amount}</TileAmount>
              <TileUnit>{tile.unit}</TileUnit>
            </TileValue>
          </Tile>
        ))}
      </Tiles>
    </Block>
  );
};

export default MealTodayStats;
